// gitlab-pat-lookup.ts
//
// Đọc PAT đã lưu cho một host non-GitHub (GitLab) từ git credential helper.
// `store-git-credential-for-host.ts` (P?) chỉ có hàm GHI (`git credential
// approve`) — không có hàm ĐỌC. Theo brief P7 Agent E: thêm hàm đọc tối
// thiểu ở đây, dùng `git credential fill` qua spawn (KHÔNG `spawnSync`, để
// nhất quán với timeout injection và không block event loop).
//
// Bảo mật: token KHÔNG BAO GIỜ được log/print/ghi vào evidence/journal.
// Caller (gitlab-merge-request-evidence-client.ts) chỉ được dùng giá trị
// trả về để set header `PRIVATE-TOKEN`, không echo lại ở đâu khác.
export interface GitlabPatLookupResult {
  found: boolean;
  /** PAT — chỉ set khi found=true. Không log giá trị này ở call site. */
  token?: string;
  username?: string;
}

export interface GitlabPatLookupDeps {
  /** Injected cho test — mặc định spawn `git credential fill`. */
  runGitCredentialFill?: (
    host: string,
    protocol: string,
  ) => Promise<{ status: number | null; stdout: string }>;
  timeoutMs?: number;
}

/**
 * `git credential fill` trả `password=<token>` nếu credential helper có sẵn
 * entry cho host này (đã lưu qua `storeGitCredentialForHost` trước đó, hoặc
 * đăng nhập thủ công của user). Không tìm thấy ⇒ `{found:false}`, KHÔNG
 * throw — caller coi đây là input hợp lệ để trả `resource_missing`.
 */
export async function lookupGitlabPat(
  host: string,
  deps: GitlabPatLookupDeps = {},
): Promise<GitlabPatLookupResult> {
  const timeoutMs = deps.timeoutMs ?? 10_000;
  const run =
    deps.runGitCredentialFill ?? ((h, p) => spawnGitCredentialFillWithStdin(h, p, timeoutMs));
  let result: { status: number | null; stdout: string };
  try {
    result = await run(host, "https");
  } catch {
    return { found: false };
  }
  if (result.status !== 0) return { found: false };

  const fields = new Map<string, string>();
  for (const line of result.stdout.split("\n")) {
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    fields.set(line.slice(0, eq), line.slice(eq + 1));
  }
  const token = fields.get("password");
  if (!token) return { found: false };
  return { found: true, token, username: fields.get("username") };
}

// `spawnAsyncCapture` (spawn-async-capture-with-timeout.ts) doesn't accept
// stdin input, so `git credential fill`'s protocol=/host=/blank-line payload
// needs a thin dedicated spawn here (mirrors that module's shape: status +
// stdout, timeout via SIGTERM).
async function spawnGitCredentialFillWithStdin(
  host: string,
  protocol: string,
  timeoutMs: number,
): Promise<{ status: number | null; stdout: string }> {
  const { spawn } = await import("node:child_process");
  return new Promise((resolve) => {
    const child = spawn("git", ["credential", "fill"], { stdio: ["pipe", "pipe", "ignore"] });
    let stdout = "";
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      child.kill("SIGTERM");
      resolve({ status: null, stdout: "" });
    }, timeoutMs);
    child.stdout.on("data", (chunk: Buffer) => {
      stdout += chunk.toString("utf8");
    });
    child.on("error", () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve({ status: null, stdout: "" });
    });
    child.on("close", (code) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve({ status: code, stdout });
    });
    child.stdin.write(`protocol=${protocol}\nhost=${host}\n\n`);
    child.stdin.end();
  });
}
